import { IPreparePayData } from '@src/api/types/payment.types';
import { queryClient } from '@src/configs/tanstack.configs';
import {
  GATEWAYS,
  MOLLIE_PAYMENT_METHOD_CODES,
  PAYMENT_REDIRECT_STATUS,
  QUERY_KEYS,
  SEARCH_KEYS,
  TRIVIAL_DEFAULT_FIRST_PERIOD_DAYS,
} from '@src/constants';
import { IMolliePaymentMethod } from '@src/interfaces/payments.interfaces';
import { IPlan } from '@src/interfaces/plans.interfaces';
import { IGateway } from '@src/interfaces/subscriptions.interfaces';

export const getAfterPaymentSearchQueries = (
  searchParams?: URLSearchParams,
) => {
  const paymentIntent = searchParams?.get(SEARCH_KEYS.PERMANENT_INTENT);
  const redirectStatus = searchParams?.get(SEARCH_KEYS.REDIRECT_STATUS);
  const planID = searchParams?.get(SEARCH_KEYS.PLAN_ID);

  return {
    paymentIntent,
    redirectStatus,
    planID: planID ? Number(planID) : undefined,
    isPaymentFailed: redirectStatus === PAYMENT_REDIRECT_STATUS.failed,
  };
};

export const onDismissPayFailAlert = () =>
  queryClient.setQueryData<IPreparePayData>(
    [QUERY_KEYS.preparePayData],
    (oldData) => {
      if (!oldData || !oldData.isPaymentFailed) return oldData;

      return {
        ...oldData,
        isPaymentFailed: false,
      };
    },
  );

export const onShowPayFailAlert = () =>
  queryClient.setQueryData<IPreparePayData>(
    [QUERY_KEYS.preparePayData],
    (oldData) => {
      if (!oldData || !oldData.isPaymentFailed) return oldData;

      return {
        ...oldData,
        isPaymentFailed: true,
      };
    },
  );

export const createUUID = () => crypto.randomUUID().replace(/-/g, '');

export const checkMolliePaymentMethods = (
  method?: IMolliePaymentMethod,
): Pick<
  IMolliePaymentMethod,
  'isAppleMethod' | 'isCardMethod' | 'isPaypalMethod'
> => ({
  isCardMethod: method?.code === MOLLIE_PAYMENT_METHOD_CODES.CREDIT_CARD,
  isPaypalMethod: method?.code === MOLLIE_PAYMENT_METHOD_CODES.PAYPAL,
  isAppleMethod: method?.code === MOLLIE_PAYMENT_METHOD_CODES.APPLE_PAY,
});

export const getIsTrial = (plan?: number) =>
  plan === TRIVIAL_DEFAULT_FIRST_PERIOD_DAYS;

export const isEventPushed = (eventName: string) =>
  window.dataLayer &&
  window.dataLayer.some((event) => event.event === eventName);

export const checkForWebSiteGateway = (gateway?: IGateway) =>
  gateway !== GATEWAYS.GOOGLE_PAY && gateway !== GATEWAYS.APPLE_PAY;

export const getPlanPrices = ({
  trial_base_price,
  trial_price,
  total_premium_base_price,
  total_premium_price = 0,
}: Partial<IPlan> = {}) => ({
  totalBasePrice: trial_base_price ?? total_premium_base_price,
  totalPrice: trial_price ?? total_premium_price,
});

export const getPlanDiscountPercent = (plan: IPlan) => {
  const { totalBasePrice, totalPrice } = getPlanPrices(plan);

  if (!totalBasePrice) return 0;

  return Math.round(((totalBasePrice - totalPrice) / totalBasePrice) * 100);
};
